import { useMemo } from 'react'
import type { Profile } from '../services/apiService'

export type ProfileCompletionStepId = 'phone' | 'industry' | 'linkedin'

export interface ProfileCompletionStep {
  id: ProfileCompletionStepId
  label: string
  completed: boolean
  linkTo: string
}

const STEPS: { id: ProfileCompletionStepId; label: string }[] = [
  { id: 'phone', label: 'Phone number' },
  { id: 'industry', label: 'Industry / field' },
  { id: 'linkedin', label: 'LinkedIn profile' },
]

const FOCUS_TARGETS: Record<ProfileCompletionStepId, string> = {
  phone: 'phone',
  industry: 'industry',
  linkedin: 'linkedinUrl',
}

function isCompleted(profile: Profile | null, id: ProfileCompletionStepId): boolean {
  if (!profile) return false
  const value = id === 'phone' ? profile.phone : id === 'industry' ? profile.industry : profile.linkedinUrl
  return typeof value === 'string' && value.trim().length > 0
}

export function useProfileCompletion(profile: Profile | null) {
  return useMemo(() => {
    const steps: ProfileCompletionStep[] = STEPS.map(({ id, label }) => ({
      id,
      label,
      completed: isCompleted(profile, id),
      linkTo: `/profile?focus=${FOCUS_TARGETS[id]}&edit=true`,
    }))
    const completedCount = steps.filter((s) => s.completed).length
    const totalCount = steps.length
    const score = totalCount === 0 ? 100 : Math.round((completedCount / totalCount) * 100)
    const isComplete = completedCount === totalCount
    return { steps, completedCount, totalCount, score, isComplete }
  }, [profile])
}

export type UseProfileCompletionReturn = ReturnType<typeof useProfileCompletion>
